/**
 * /blog/:slug — public single blog article. 404s for draft/missing posts.
 */
import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import RelatedArticles from "@/components/RelatedArticles";
import RainwaterCluster from "@/components/RainwaterCluster";
import { useSEO, SEO_DEFAULTS, buildFaqPageLd, buildBlogPostingLd } from "@/lib/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not-found | error

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    (async () => {
      try {
        const r = await fetch(`${API}/api/blog/posts/${encodeURIComponent(slug)}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        if (r.status === 404) { setStatus("not-found"); return; }
        if (!r.ok) { setStatus("error"); return; }
        const d = await r.json();
        if (controller.signal.aborted) return;
        setPost(d);
        setStatus("ok");
      } catch {
        if (controller.signal.aborted) return;
        setStatus("error");
      }
    })();
    return () => controller.abort();
  }, [slug]);

  // Feb 2026 sitemap consolidation — every blog article now emits a
  // self-referencing canonical + og:url on the AWARE domain, plus an
  // `article` OpenGraph type for social unfurlers. `useSEO` strips any
  // query-string (?utm_…) from the canonical automatically.
  useSEO({
    title: post ? (post.seo_title || post.title) : "Article",
    description: post ? (post.seo_description || post.excerpt || "") : "",
    url: `${SEO_DEFAULTS.baseUrl}/blog/${slug}`,
    image: post?.featured_image_url || SEO_DEFAULTS.image,
    type: "article",
    // Only definitively dead states are noindexed. The transient "loading"
    // state must NOT emit noindex — Google's renderer can snapshot the DOM
    // before the post fetch resolves and permanently exclude the article
    // (seen in Search Console: "'noindex' detected in 'robots' meta tag").
    noindex: status === "error" || status === "not-found",
    // Jun 2026 — articles emit BlogPosting + ONE valid FAQPage. The raw
    // `{question, answer}` rows must never be passed straight through
    // (they became ~18 typeless JSON-LD blocks per article).
    jsonLd: [
      buildBlogPostingLd(post, slug),
      buildFaqPageLd(post?.faq_schema),
    ].filter(Boolean),
  });

  if (status === "not-found") return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-10 lg:pb-14" data-testid="blog-post">
        {status === "loading" && <div className="text-slate-500" data-testid="blog-post-loading">Loading…</div>}
        {status === "error" && <div className="text-red-700">Sorry — failed to load this article.</div>}
        {post && (
          <>
            {/* Phase 2L-A36 — Calm in-house fallback for posts without
                a `featured_image_url`. Real GetAutoSEO-published hero
                images continue to render exactly as before. */}
            <picture data-testid="blog-post-image">
              {!post.featured_image_url && (
                <source srcSet="/img/v2/blog-fallback.webp" type="image/webp" />
              )}
              <img
                src={post.featured_image_url || "/img/v2/blog-fallback.jpg"}
                alt={post.featured_image_url ? post.title : ""}
                loading="eager"
                decoding="async"
                width="1200"
                height="675"
                className="w-full max-h-[480px] object-cover rounded-2xl ring-1 ring-slate-200"
              />
            </picture>
            <div className="mt-6 mb-4 flex items-center gap-3 text-xs text-slate-500">
              {post.category && (
                <span className="text-[11px] uppercase tracking-[0.18em] text-cyan-700 font-bold">{post.category}</span>
              )}
              <span>{fmtDate(post.published_at)}</span>
              {post.author && <span>· {post.author}</span>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            {post.excerpt && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>}
            <article
              className="prose prose-slate prose-headings:font-display prose-a:text-[#1F4E79] mt-8 max-w-none"
              data-testid="blog-post-content"
              // Phase 2L-security-M3 (Feb-2026): backend sanitises via
              // bleach before storage; we also run DOMPurify here for
              // defence-in-depth so a future server-side bypass still
              // can't reach the customer DOM with <script>.
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }}
            />

            {/* Tags */}
            {Array.isArray(post.tags) && post.tags.length > 0 && (
              <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
                {post.tags.map((t) => (
                  <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-sky-50 text-sky-800 ring-1 ring-sky-200">{t}</span>
                ))}
              </div>
            )}

            {/* CTAs */}
            <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-[#1F4E79] to-[#3FA9E0] text-white" data-testid="blog-post-cta">
              <div className="text-lg font-semibold">Have a question about your water?</div>
              <p className="text-sm opacity-95 mt-1">Book a test or chat to the Aware team.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/book-water-test" className="px-4 py-2 rounded-lg bg-white text-[#1F4E79] font-semibold text-sm hover:bg-slate-50 transition">
                  Book a water test
                </Link>
                <Link to="/contact" className="px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 ring-1 ring-white/30 text-white font-semibold text-sm transition">
                  Contact the team
                </Link>
              </div>
            </div>

            {/* Sep 2026 — Rainwater Hub Cluster: durable app-rendered
                sibling links + Tank 360 CTA. Renders nothing outside
                the rainwater cluster. */}
            <RainwaterCluster slug={post.slug} />

            {/* Phase 2L-A50 — Related articles (same category → shared
                tags → most-recent fallback). Renders nothing when no
                published peers exist. */}
            <RelatedArticles slug={post.slug} />

            <div className="mt-8">
              <Link to="/blog" className="text-sm text-[#1F4E79] hover:underline">← All articles</Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
